import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSiteInfo } from "@/hooks/useSiteInfo";
import type { NavLinkItem, NavSubLink } from "@/lib/types";

export default function Footer() {
  const { t } = useTranslation();
  const site = useSiteInfo();
  const footerLinksRaw = t("common.footer.links", { returnObjects: true }) as unknown as NavLinkItem[];
  const footerLinks = Array.isArray(footerLinksRaw) ? footerLinksRaw : [];

  return (
    <footer className="bg-foreground-950 text-background-50">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3">
              <span className="h-[46px] w-[80px] rounded-md bg-background-50 flex items-center justify-center overflow-hidden">
                <img
                  src="https://storage.readdy-site.link/project_files/e8666665-2242-48cf-9593-4ebcd3ad786e/6437e91a-c2b1-4345-9a35-15b2da6f9f43_compressed_logo.webp"
                  alt={site.brandKo}
                  className="h-full w-full object-contain"
                  style={{ transform: "scaleY(1.18)" }}
                />
              </span>
              <div className="leading-tight">
                <div className="font-heading font-bold text-lg">{site.brandKo}</div>
                <div className="text-[11px] tracking-[0.2em] uppercase text-background-50/60">
                  {site.brandEn}
                </div>
              </div>
            </div>
            <p className="mt-6 text-sm text-background-50/70 leading-relaxed max-w-md">
              {t("common.footer.desc")}
            </p>
            <div className="mt-8 flex items-center gap-3">
              {["ri-global-line", "ri-linkedin-fill", "ri-youtube-fill", "ri-mail-fill"].map(
                (icon) => (
                  <button
                    key={icon}
                    type="button"
                    className="w-10 h-10 rounded-full border border-background-50/30 hover:border-accent-400 hover:text-accent-300 flex items-center justify-center transition-colors cursor-pointer"
                  >
                    <i className={`${icon} text-sm`} />
                  </button>
                ),
              )}
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="text-[11px] tracking-[0.2em] uppercase text-background-50/50 font-semibold mb-5">
              {t("common.footer.sitemapTitle")}
            </div>
            <div className="flex flex-col gap-2">
              {footerLinks.map((l) => (
                <div key={l.label}>
                  <Link
                    to={l.to}
                    className="text-sm font-semibold text-background-50/85 hover:text-accent-300 transition-colors cursor-pointer"
                  >
                    {l.label}
                  </Link>
                  {l.sub && (
                    <div className="ml-3 mt-1 flex flex-col gap-1">
                      {l.sub.map((s: NavSubLink) => (
                        <Link
                          key={s.to}
                          to={s.to}
                          className="text-xs text-background-50/55 hover:text-accent-300 transition-colors cursor-pointer"
                        >
                          {s.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="text-[11px] tracking-[0.2em] uppercase text-background-50/50 font-semibold mb-5">
              {t("common.footer.contactTitle")}
            </div>
            <ul className="flex flex-col gap-3 text-sm">
              <li className="flex items-start gap-3">
                <span className="w-5 h-5 mt-0.5 flex items-center justify-center text-accent-400">
                  <i className="ri-map-pin-2-line" />
                </span>
                <span className="text-background-50/85 leading-relaxed">{site.address}</span>
              </li>
              <li>
                <a
                  href={site.telLink}
                  className="flex items-center gap-3 text-background-50/85 hover:text-accent-300 cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center text-accent-400">
                    <i className="ri-phone-line" />
                  </span>
                  {t("common.footer.tel")} · {site.tel}
                </a>
              </li>
              <li className="flex items-center gap-3 text-background-50/85">
                <span className="w-5 h-5 flex items-center justify-center text-accent-400">
                  <i className="ri-printer-line" />
                </span>
                {t("common.footer.fax")} · {site.fax}
              </li>
              <li>
                <a
                  href={site.emailLink}
                  className="flex items-center gap-3 text-background-50/85 hover:text-accent-300 cursor-pointer"
                >
                  <span className="w-5 h-5 flex items-center justify-center text-accent-400">
                    <i className="ri-mail-line" />
                  </span>
                  {site.email}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-background-50/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-5">
            <p className="text-xs text-background-50/55">
              © {new Date().getFullYear()} {site.brandKo}. {t("common.footer.rights")}
            </p>
            <Link
              to="/admin/login"
              className="text-[10px] text-background-50/25 hover:text-background-50/55 transition-colors cursor-pointer"
            >
              {t("common.footer.admin")}
            </Link>
          </div>
          <div className="flex items-center gap-5 text-xs text-background-50/70">
            <a href="#" className="hover:text-accent-300 cursor-pointer">
              {t("common.footer.privacy")}
            </a>
            <a href="#" className="hover:text-accent-300 cursor-pointer">
              {t("common.footer.terms")}
            </a>
            <a href="#" className="hover:text-accent-300 cursor-pointer">
              {t("common.footer.sitemap")}
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}